import { useState } from "react";
import IntranetLayout from "@/components/IntranetLayout";
import ModuleHeader from "@/components/voff/ModuleHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Star, Search, Copy, Sparkles } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { name: "📁 CHARTER PARTY", subs: ["Hire & Payment","Off-Hire","Fuel & Bunkers","Redelivery","Maintenance & Condition","Crew & Manning","Trading Limits","War & ISPS Risk","DP Requirements","CTV-Specific"] },
  { name: "📁 NDA / CONFIDENTIALITY", subs: ["Standard NDA","Mutual NDA","One-way NDA"] },
  { name: "📁 LIABILITY & INDEMNITY", subs: ["Knock-for-Knock","Consequential Loss","Liability Caps","Himalaya"] },
  { name: "📁 FORCE MAJEURE", subs: ["Standard FM","Pandemic/Health FM"] },
  { name: "📁 TERMINATION", subs: ["Convenience","Default","Change of Control"] },
  { name: "📁 DISPUTE RESOLUTION", subs: ["LMAA Arbitration","ICC Arbitration","Expert Determination"] },
];

const CLAUSES = [
  { id: 1, title: "Off-Hire — Equipment Failure Only", category: "Charter Party / Off-Hire", source: "VOFF Standard / Rev 2", uses: 4, strength: 5,
    text: "The Vessel shall be off-hire during any period when she is unable to perform the Services solely by reason of failure of the Vessel's machinery or equipment, and only for the duration of such inability. For the avoidance of doubt, weather standby, port delays, operational standby ordered by the Charterer, and waiting time shall not constitute off-hire.",
    notes: "VOFF preferred; rejected by Bourbon (2024) but accepted by ExxonMobil (2025). Strong protection for Owner." },
  { id: 2, title: "Knock-for-Knock — Personnel & Property", category: "Liability / Knock-for-Knock", source: "OCIMF Standard", uses: 6, strength: 5,
    text: "Each party shall indemnify and hold harmless the other party from and against all claims, losses and damages arising from injury to or death of its own personnel and damage to its own property, regardless of negligence or breach of duty by the other party.",
    notes: "Standard offshore practice. Universally accepted. Keep in all charters." },
  { id: 3, title: "LMAA Arbitration — Standard", category: "Dispute Resolution / LMAA", source: "LMAA 2021 Terms", uses: 8, strength: 5,
    text: "Any dispute arising out of or in connection with this Agreement shall be referred to arbitration in London in accordance with the LMAA Terms current at the time when the arbitration proceedings are commenced. The arbitration shall be conducted before three arbitrators, one nominated by each party and the third selected by the two appointed arbitrators.",
    notes: "VOFF default for all maritime contracts. London-seat. English law." },
  { id: 4, title: "Liability Cap — 1x Contract Value", category: "Liability / Liability Caps", source: "VOFF Standard / Rev 1", uses: 3, strength: 4,
    text: "Notwithstanding any other provision of this Agreement, each party's aggregate liability under or in connection with this Agreement (whether in contract, tort or otherwise) shall not exceed an amount equal to one (1) times the total Contract value, except in cases of fraud or wilful misconduct.",
    notes: "Acceptable for most service agreements. For high-value charters, consider raising to 1.5x." },
  { id: 5, title: "Convenience Termination — 60 Days", category: "Termination / Convenience", source: "VOFF Standard", uses: 5, strength: 4,
    text: "Either party may terminate this Agreement for convenience upon sixty (60) days' written notice to the other party. Upon such termination, all hire accrued to the date of termination shall be paid in full.",
    notes: "VOFF prefers 60+ days; Bo to push back on any proposal under 30 days." },
  { id: 6, title: "DP Requirements — DP2 Class with Annual Trial", category: "Charter Party / DP Requirements", source: "VOFF Standard", uses: 4, strength: 5,
    text: "The Vessel shall maintain ABS DP-2 notation throughout the charter period. Annual DP trials shall be conducted in accordance with IMCA M 190 / IMO MSC.1/Circ.1580 and a copy of the trial report provided to Charterer within 14 days of completion.",
    notes: "Standard for FPSO support work. ExxonMobil and Azule both accept verbatim." },
];

const ClauseLibrary = () => {
  const [selected, setSelected] = useState(CLAUSES[0]);
  const [search, setSearch] = useState("");
  const filtered = CLAUSES.filter(c => !search || c.title.toLowerCase().includes(search.toLowerCase()) || c.text.toLowerCase().includes(search.toLowerCase()));

  return (
    <IntranetLayout>
      <ModuleHeader title="Clause Library" subtitle="Charter Party & Contract Clause Precedents"
        stats={[{ label: "Clauses", value: 64 }, { label: "Most used", value: "Off-Hire" }]} />

      <div className="grid lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-3 border-[#D0DDE6] h-fit">
          <CardContent className="p-4">
            {CATEGORIES.map((c) => (
              <div key={c.name} className="mb-3">
                <div className="font-bold text-xs text-[#00283C] mb-1">{c.name}</div>
                <ul className="text-xs space-y-0.5 ml-4 text-[#6B8A9A]">{c.subs.map(s => <li key={s} className="hover:text-[#288CC8] cursor-pointer">└ {s}</li>)}</ul>
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="lg:col-span-5 space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-[#6B8A9A]" />
            <Input placeholder="Search clauses (e.g., 'off-hire', 'arbitration')..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
          </div>
          {filtered.map((c) => (
            <Card key={c.id} className={`border cursor-pointer transition ${selected.id === c.id ? "border-[#288CC8] bg-[#288CC8]/5" : "border-[#D0DDE6] hover:border-[#288CC8]/50"}`} onClick={() => setSelected(c)}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-1">
                  <div className="font-bold text-sm text-[#00283C]">{c.title}</div>
                  <div className="flex">{Array.from({length: c.strength}).map((_, i) => <Star key={i} className="w-3 h-3 fill-[#E8A020] text-[#E8A020]" />)}</div>
                </div>
                <div className="text-xs text-[#6B8A9A]">{c.category} • {c.source} • Used in {c.uses} contracts</div>
                <p className="text-xs mt-2 line-clamp-2 text-[#0D1E2B]">{c.text}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="lg:col-span-4 border-[#D0DDE6] h-fit sticky top-4">
          <CardContent className="p-5 space-y-3">
            <div>
              <div className="text-xs uppercase text-[#6B8A9A]">Selected Clause</div>
              <div className="font-bold text-[#00283C]">{selected.title}</div>
            </div>
            <div className="bg-[#F4F7FA] p-3 rounded text-sm leading-relaxed border border-[#D0DDE6] text-[#0D1E2B]">"{selected.text}"</div>
            <Button size="sm" variant="outline" className="w-full" onClick={() => { navigator.clipboard.writeText(selected.text); toast.success("Copied"); }}>
              <Copy className="w-3 h-3 mr-1" />Copy clause
            </Button>
            <div className="bg-[#288CC8]/5 border border-[#288CC8]/20 p-3 rounded text-xs">
              <div className="font-bold text-[#288CC8] flex items-center gap-1 mb-1"><Sparkles className="w-3 h-3" />VOFF Notes</div>
              <p>{selected.notes}</p>
            </div>
            <div className="space-y-1">
              <Button onClick={() => toast.success("Action recorded: Get AI Analysis")} size="sm" variant="outline" className="w-full"><Sparkles className="w-3 h-3 mr-1" />Get AI Analysis</Button>
              <Button onClick={() => toast.success("Created: Generate Alternative", { description: "New entry added." })} size="sm" variant="outline" className="w-full">Generate Alternative</Button>
              <Button onClick={() => toast.success("Action recorded: Compare Versions")} size="sm" variant="outline" className="w-full">Compare Versions</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </IntranetLayout>
  );
};
export default ClauseLibrary;
